import {create } from 'zustand';
import type {
  CombatActionResponseDTO,
  CombatLogEntryDTO,
  CombatPosition,
  GameConfigDTO,
  GameStateDTO,
} from '@hexcrawl/shared-utils';
import { EntityStatus } from '@hexcrawl/shared-utils';
import { useNpcStore } from './npcStore';

interface GameState {
  gameState: GameStateDTO | null;
  gameConfig: GameConfigDTO | null;
  setGameState: (gameState: GameStateDTO | null) => void;
  setGameConfig: (gameConfig: GameConfigDTO | null) => void;
  clear: () => void;
}

export const useGameStore = create<GameState>()((set) => ({
  gameState: null,
  gameConfig: null,
  setGameState: (gameState) => set({ gameState: gameState }),
  setGameConfig: (gameConfig) => set({ gameConfig }),
  clear: () => {
    set({ gameState: null, gameConfig: null });
    useNpcStore.getState().clear();
  },
}));

/**
 * Spend one unit of an item instance in the character's bag, dropping the row once its
 * last unit goes. The combat use-item response is a CombatActionResponse and carries no
 * inventory, so the client mirrors the server's bookkeeping; the next full state fetch
 * confirms it.
 */
export const spendInventoryUnit = (instanceId: string) => {
  const gs = useGameStore.getState().gameState;
  if (!gs?.character) return;

  const inventory = (gs.character.inventory ?? [])
    .map(i => (i.id === instanceId ? { ...i, quantity: i.quantity - 1 } : i))
    .filter(i => i.quantity > 0);

  useGameStore.getState().setGameState({
    ...gs,
    character: { ...gs.character, inventory },
  });
};

// --- Incremental state update helpers for queued playback ---

/** Update a single entity's position in the combat state. */
export const applyEntityPosition = (entityId: string, position: CombatPosition) => {
  const gs = useGameStore.getState().gameState;
  if (!gs?.activeCombat) return;

  const updatedCombat = { ...gs.activeCombat };
  const entityState = updatedCombat.entityStates.find(e => e.entityId === entityId);
  if (entityState) {
    entityState.position = position;
  }

  useGameStore.getState().setGameState({
    ...gs,
    activeCombat: updatedCombat,
  });
};

/** Apply a relative HP/MP change to an entity's vitals. */
export const applyEntityVitalsChange = (entityId: string, hpChange: number, mpChange: number) => {
  const gs = useGameStore.getState().gameState;
  if (!gs?.activeCombat) return;

  const updatedCombat = { ...gs.activeCombat };
  let updatedCharacter = gs.character;

  if (updatedCharacter && entityId === updatedCharacter.id) {
    updatedCharacter = { ...updatedCharacter };
    if (hpChange !== 0) updatedCharacter.currentHP = Math.max(0, (updatedCharacter.currentHP ?? 0) + hpChange);
    if (mpChange !== 0) updatedCharacter.currentMP = Math.max(0, (updatedCharacter.currentMP ?? 0) + mpChange);
  } else if (updatedCombat.enemyStates) {
    const enemyIdx = updatedCombat.enemyStates.findIndex(e => e.entityId === entityId);
    if (enemyIdx >= 0) {
      const updatedEnemyStates = [...updatedCombat.enemyStates];
      const enemy = { ...updatedEnemyStates[enemyIdx] };
      if (hpChange !== 0) enemy.currentHP = Math.max(0, enemy.currentHP + hpChange);
      if (mpChange !== 0) enemy.currentMP = Math.max(0, enemy.currentMP + mpChange);
      updatedEnemyStates[enemyIdx] = enemy;
      updatedCombat.enemyStates = updatedEnemyStates;
    } else {
      const npc = useNpcStore.getState().npcs[entityId];
      if (npc) {
        useNpcStore.getState().setNpc({
          ...npc,
          currentHP: Math.max(0, (npc.currentHP ?? 0) + hpChange),
          currentMP: Math.max(0, (npc.currentMP ?? 0) + mpChange),
        });
      }
    }
  }

  useGameStore.getState().setGameState({
    ...gs,
    character: updatedCharacter,
    activeCombat: updatedCombat,
  });
};

/** Update a single entity's status (e.g. mark as dead). */
export const applyEntityStatus = (entityId: string, status: EntityStatus) => {
  const gs = useGameStore.getState().gameState;
  if (!gs?.activeCombat) return;

  const updatedCombat = { ...gs.activeCombat };
  const entityState = updatedCombat.entityStates.find(e => e.entityId === entityId);
  if (entityState) {
    entityState.status = status;
  }

  useGameStore.getState().setGameState({
    ...gs,
    activeCombat: updatedCombat,
  });
};

/** Append a single log entry to the combat log. */
export const appendCombatLogEntry = (entry: CombatLogEntryDTO) => {
  const gs = useGameStore.getState().gameState;
  if (!gs?.activeCombat) return;

  const updatedCombat = { ...gs.activeCombat };
  updatedCombat.log = [...updatedCombat.log, entry];

  useGameStore.getState().setGameState({
    ...gs,
    activeCombat: updatedCombat,
  });
};


/**
 * Apply the full response as a final reconciliation step.
 * Ensures all entity state deltas and combat deltas are applied,
 * correcting any discrepancies from intermediate calculations.
 */
export const reconcileCombatState = (result: CombatActionResponseDTO) => {
  const gs = useGameStore.getState().gameState;
  if (!gs?.activeCombat) return;

  const updatedCombat = { ...gs.activeCombat };
  let updatedCharacter = gs.character;

  // Apply combat delta
  if (result.combatDelta) {
    if (result.combatDelta.round !== undefined) updatedCombat.round = result.combatDelta.round;
    if (result.combatDelta.turnIndex !== undefined) updatedCombat.turnIndex = result.combatDelta.turnIndex;
    if (result.combatDelta.turnHasMoved !== undefined) updatedCombat.turnHasMoved = result.combatDelta.turnHasMoved;
    if (result.combatDelta.turnHasUsedAbility !== undefined) updatedCombat.turnHasUsedAbility = result.combatDelta.turnHasUsedAbility;
    if (result.combatDelta.status !== undefined) updatedCombat.status = result.combatDelta.status;
  }

  // Apply all entity state deltas (final authoritative values)
  if (result.entityStateDeltas) {
    for (const delta of result.entityStateDeltas) {
      const entityState = updatedCombat.entityStates.find(e => e.entityId === delta.entityId);
      if (entityState) {
        if (delta.position) entityState.position = delta.position;
        if (delta.status !== undefined) entityState.status = delta.status;
        // Sent whole, so a buff that expired this action arrives as its absence.
        if (delta.activeEffects) entityState.activeEffects = delta.activeEffects;
        if (delta.currentHP !== undefined || delta.currentMP !== undefined) {
          if (updatedCharacter && delta.entityId === updatedCharacter.id) {
            if (delta.currentHP !== undefined) updatedCharacter.currentHP = delta.currentHP;
            if (delta.currentMP !== undefined) updatedCharacter.currentMP = delta.currentMP;
          } else if (updatedCombat.enemyStates) {
            const enemyState = updatedCombat.enemyStates.find(e => e.entityId === delta.entityId);
            if (enemyState) {
              if (delta.currentHP !== undefined) enemyState.currentHP = delta.currentHP;
              if (delta.currentMP !== undefined) enemyState.currentMP = delta.currentMP;
            } else {
              const npc = useNpcStore.getState().npcs[delta.entityId];
              if (npc) {
                useNpcStore.getState().setNpc({
                  ...npc,
                  ...(delta.currentHP !== undefined ? { currentHP: delta.currentHP } : {}),
                  ...(delta.currentMP !== undefined ? { currentMP: delta.currentMP } : {}),
                });
              }
            }
          }
        }
      }
    }
  }

  // Ensure all log entries are present (deduplicate by timestamp)
  if (result.actions?.length) {
    const existingTimestamps = new Set(updatedCombat.log.map(e => e.timestamp));
    const newEntries = result.actions.filter(a => !existingTimestamps.has(a.timestamp));
    if (newEntries.length > 0) {
      updatedCombat.log = [...updatedCombat.log, ...newEntries];
    }
  }

  useGameStore.getState().setGameState({
    ...gs,
    character: updatedCharacter,
    activeCombat: updatedCombat,
  });
};
